import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getAdminGalleryFolders, createGalleryFolder, updateGalleryFolder, deleteGalleryFolder, uploadFolderCoverImage, getMediaUrl } from '../../services/api.jsx';
import { useAuth } from '../../contexts/AuthContext';

const GalleryFolderManager = () => {
    const [folders, setFolders] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [editingFolder, setEditingFolder] = useState(null);
    const [newFolder, setNewFolder] = useState({ description_de: '', description_en: '', description_it: '' });
    const [coverImageFile, setCoverImageFile] = useState(null);
    const [coverPreview, setCoverPreview] = useState(null);
    const [activeLangNew, setActiveLangNew] = useState('de');
    const [activeLangEdit, setActiveLangEdit] = useState('de');

    const { token } = useAuth();

    // Helper: sanitize description values (avoid literal null / 'null')
    const sanitizeDesc = (v) => {
        if (v === null || v === undefined) return '';
        if (typeof v === 'string' && v.trim().toLowerCase() === 'null') return '';
        return v;
    };
    const sanitizeFolderObject = (folder) => ({
        ...folder,
        description_de: sanitizeDesc(folder.description_de),
        description_en: sanitizeDesc(folder.description_en),
        description_it: sanitizeDesc(folder.description_it),
    });

    const fetchFolders = useCallback(async () => {
        setIsLoading(true);
        try {
            const fetchedFolders = await getAdminGalleryFolders(token);
            setFolders(fetchedFolders);
        } catch (err) {
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    }, [token]);

    useEffect(() => {
        fetchFolders();
    }, [fetchFolders]);

    const resetMessages = () => {
        setError(null);
        setSuccessMessage(null);
    };

    const validateAllDescriptions = (obj) => {
        const fields = [
            { key: 'description_de', label: 'Deutsch' },
            { key: 'description_en', label: 'Englisch' },
            { key: 'description_it', label: 'Italienisch' },
        ];
        const missing = fields.filter(f => !obj[f.key] || !obj[f.key].toString().trim());
        if (missing.length) {
            setError('Alle drei Sprachen sind erforderlich: ' + missing.map(m => m.label).join(', '));
            return false;
        }
        return true;
    };

    const validateNewFolder = () => {
        if (!validateAllDescriptions(newFolder)) return false;
        if (!coverImageFile) {
            setError('Bitte ein Titelbild auswählen.');
            return false;
        }
        return true;
    };

    const handleCreateFolder = async (e) => {
        e.preventDefault();
        resetMessages();
        if (!validateNewFolder()) return;
        setIsLoading(true);
        try {
            const formData = new FormData();
            formData.append('description_de', newFolder.description_de);
            formData.append('description_en', newFolder.description_en);
            formData.append('description_it', newFolder.description_it);
            if (coverImageFile) {
                formData.append('cover_image', coverImageFile);
            }
            await createGalleryFolder(formData, token);
            setNewFolder({ description_de: '', description_en: '', description_it: '' });
            setCoverImageFile(null);
            setCoverPreview(null);
            setSuccessMessage('Ordner erfolgreich erstellt.');
            fetchFolders();
        } catch (err) {
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleUpdateFolder = async (folderId) => {
        resetMessages();
        if (!validateAllDescriptions(editingFolder)) return;
        setIsLoading(true);
        try {
            const formData = new FormData();
            formData.append('description_de', editingFolder.description_de);
            formData.append('description_en', editingFolder.description_en);
            formData.append('description_it', editingFolder.description_it);

            await updateGalleryFolder(folderId, formData, token);

            if (coverImageFile) {
                const coverFormData = new FormData();
                coverFormData.append('cover_image', coverImageFile);
                await uploadFolderCoverImage(folderId, coverFormData, token);
            }

            setEditingFolder(null);
            setCoverImageFile(null);
            setActiveLangEdit('de');
            setSuccessMessage('Ordner aktualisiert.');
            fetchFolders();
        } catch (err) {
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleDeleteFolder = async (folderId) => {
        if (window.confirm("Möchten Sie diesen Ordner wirklich löschen?")) {
            resetMessages();
            setIsLoading(true);
            try {
                await deleteGalleryFolder(folderId, token);
                setSuccessMessage('Ordner gelöscht.');
                fetchFolders();
            } catch (err) {
                setError(err.message);
            } finally {
                setIsLoading(false);
            }
        }
    };

    const handleEditClick = (folder) => {
        setEditingFolder(sanitizeFolderObject(folder));
        setCoverImageFile(null);
    };

    const LangTabs = ({ active, onChange, compact = false, values }) => {
        const tabs = [
            { code: 'de', label: 'DE' },
            { code: 'en', label: 'EN' },
            { code: 'it', label: 'IT' },
        ];
        return (
            <div className={`inline-flex rounded-xl border border-neutral-200 bg-neutral-50 overflow-hidden mb-3 ${compact ? 'text-xs' : 'text-sm'}`}>
                {tabs.map(tab => (
                    <button
                        key={tab.code}
                        type="button"
                        onClick={() => onChange(tab.code)}
                        className={`px-3 py-1.5 font-medium transition-colors ${active === tab.code ? 'bg-white text-neutral-900 shadow-inner' : 'text-neutral-500 hover:text-neutral-800 hover:bg-neutral-100'}`}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>
        );
    };

    const FolderCard = ({ folder }) => {
        const isEditing = editingFolder && editingFolder.id === folder.id;
        const activeLang = activeLangEdit;
        const valueByLang = {
            de: sanitizeDesc(editingFolder?.description_de ?? folder.description_de),
            en: sanitizeDesc(editingFolder?.description_en ?? folder.description_en),
            it: sanitizeDesc(editingFolder?.description_it ?? folder.description_it),
        };
        const coverUrl = folder.cover_image_path ? getMediaUrl(folder.cover_image_path) : null;
        const editInputRef = useRef(null);

        useEffect(() => {
            if (isEditing && editInputRef.current) {
                // Focus and move caret to end without scrolling the entire page
                const el = editInputRef.current;
                el.focus({ preventScroll: true });
                const len = el.value.length;
                try { el.setSelectionRange(len, len); } catch (_) { /* ignore */ }
            }
        }, [isEditing, activeLang]);

        return (
            <div className="group bg-white border border-neutral-200 rounded-xl shadow-sm hover:shadow-md transition-all overflow-hidden flex flex-col">
                <div className="relative h-40 bg-neutral-100 overflow-hidden">
                    {coverUrl ? (
                        <img src={coverUrl} alt="Cover" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    ) : (
                        <div className="w-full h-full flex items-center justify-center text-neutral-300">
                            <svg className="w-12 h-12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 7v10a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-6l-2-2H5a2 2 0 0 0-2 2Z" /></svg>
                        </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-transparent opacity-60" />
                    {!isEditing && (
                        <div className="absolute top-2 left-2 bg-black/50 text-white text-xs px-2 py-1 rounded-md backdrop-blur-sm">Ordner</div>
                    )}
                </div>
                <div className="p-4 flex-1 flex flex-col">
                    {isEditing ? (
                        <>
                            <LangTabs active={activeLang} onChange={setActiveLangEdit} values={editingFolder} />
                            <input
                                ref={editInputRef}
                                type="text"
                                value={sanitizeDesc(editingFolder[`description_${activeLang}`])}
                                onChange={(e) => setEditingFolder({ ...editingFolder, [`description_${activeLang}`]: e.target.value })}
                                placeholder={`Beschreibung (${activeLang.toUpperCase()})`}
                                className="w-full p-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500 mb-3"
                                autoComplete="off"
                                autoCorrect="off"
                                spellCheck={false}
                            />
                            <label className="block text-sm font-medium text-neutral-700 mb-1">Titelbild aktualisieren</label>
                            <input
                                type="file"
                                onChange={(e) => setCoverImageFile(e.target.files[0])}
                                className="w-full text-sm file:mr-3 file:py-2 file:px-3 file:border file:border-neutral-200 file:rounded-lg file:bg-neutral-50 file:text-neutral-700 hover:file:bg-neutral-100 cursor-pointer mb-4"
                            />
                            <div className="mt-auto flex gap-2">
                                <button onClick={() => handleUpdateFolder(folder.id)} type="button" className="flex-1 inline-flex justify-center items-center h-10 px-4 rounded-lg bg-green-600 hover:bg-green-500 text-white font-medium shadow-sm">Speichern</button>
                                <button onClick={() => setEditingFolder(null)} type="button" className="flex-1 inline-flex justify-center items-center h-10 px-4 rounded-lg bg-neutral-200 hover:bg-neutral-300 text-neutral-800 font-medium">Abbrechen</button>
                            </div>
                        </>
                    ) : (
                        <>
                            <h3 className="text-lg font-semibold text-neutral-900 line-clamp-2 mb-2">{valueByLang.de || valueByLang.en || valueByLang.it || '—'}</h3>
                            <div className="text-xs text-neutral-500 space-y-1 mb-4">
                                {['de', 'en', 'it'].map(code => (
                                    <div key={code} className="flex items-start gap-2">
                                        <span className="uppercase text-[10px] tracking-wide font-semibold text-neutral-400 mt-1 w-5">{code}</span>
                                        <span className="flex-1 text-neutral-600 line-clamp-2">{valueByLang[code] || <span className="italic text-neutral-400">leer</span>}</span>
                                    </div>
                                ))}
                            </div>
                            <div className="mt-auto flex gap-2">
                                <button onClick={() => handleEditClick(folder)} className="flex-1 inline-flex justify-center items-center h-9 px-3 rounded-lg bg-primary-600 hover:bg-primary-500 text-white text-sm font-medium shadow-sm">Bearbeiten</button>
                                <button onClick={() => handleDeleteFolder(folder.id)} className="flex-1 inline-flex justify-center items-center h-9 px-3 rounded-lg bg-red-600 hover:bg-red-500 text-white text-sm font-medium shadow-sm">Löschen</button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        );
    };

    // Cover preview effect & cleanup
    useEffect(() => {
        if (coverImageFile) {
            const url = URL.createObjectURL(coverImageFile);
            setCoverPreview(url);
            return () => URL.revokeObjectURL(url);
        }
    }, [coverImageFile]);

    return (
        <div className="max-w-7xl mx-auto px-4 py-10">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
                <div>
                    <h2 className="text-3xl font-bold tracking-tight text-neutral-900 mb-3">Galerie-Ordner Verwaltung</h2>
                    <p className="text-neutral-600 max-w-2xl text-sm leading-relaxed">Verwalten Sie die Ordnerstruktur der öffentlichen Galerie. Titelbilder helfen bei der visuellen Orientierung.</p>
                </div>
                <div className="flex items-center gap-3 text-sm">
                    {isLoading && <span className="inline-flex items-center gap-2 text-primary-600"><span className="w-3 h-3 rounded-full border-2 border-primary-300 border-t-primary-600 animate-spin"></span>Lädt...</span>}
                </div>
            </div>

            {(error || successMessage) && (
                <div className="mb-8 space-y-3">
                    {error && (
                        <div className="p-4 rounded-xl border border-red-200 bg-red-50 text-red-700 text-sm flex items-start gap-3">
                            <svg className="w-5 h-5 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 9v4m0 4h.01" /><path d="M12 2 2 22h20L12 2Z" /></svg>
                            <div className="flex-1">{error}</div>
                            <button onClick={() => setError(null)} className="text-xs font-medium underline decoration-dotted">Schließen</button>
                        </div>
                    )}
                    {successMessage && (
                        <div className="p-4 rounded-xl border border-green-200 bg-green-50 text-green-700 text-sm flex items-start gap-3">
                            <svg className="w-5 h-5 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="m4 12 6 6 10-14" /></svg>
                            <div className="flex-1">{successMessage}</div>
                            <button onClick={() => setSuccessMessage(null)} className="text-xs font-medium underline decoration-dotted">OK</button>
                        </div>
                    )}
                </div>
            )}

            <form onSubmit={handleCreateFolder} className="mb-12 bg-white border border-neutral-200 rounded-2xl shadow-sm p-6 md:p-8 relative overflow-hidden">
                <div className="absolute inset-0 pointer-events-none opacity-[0.03] bg-[radial-gradient(circle_at_30%_20%,#3b82f6,transparent_60%)]" />
                <div className="relative z-10">
                    <h3 className="text-xl font-semibold text-neutral-900 mb-5 flex items-center gap-2">
                        <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-primary-600 text-white shadow-sm">+</span>
                        Neuen Ordner erstellen
                    </h3>
                    <LangTabs active={activeLangNew} onChange={setActiveLangNew} values={newFolder} />
                    <div className="grid md:grid-cols-2 gap-6">
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-neutral-700 mb-1">Beschreibung ({activeLangNew.toUpperCase()}) <span className="text-red-500">*</span></label>
                                <input
                                    type="text"
                                    value={newFolder[`description_${activeLangNew}`]}
                                    onChange={(e) => setNewFolder({ ...newFolder, [`description_${activeLangNew}`]: e.target.value })}
                                    className="w-full p-2.5 rounded-lg border border-neutral-300 bg-white/70 focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:border-primary-500 text-sm"
                                    placeholder={`Beschreibung eingeben (${activeLangNew.toUpperCase()})`}
                                />
                            </div>
                            <div className="text-[11px] text-neutral-500 leading-relaxed">
                                Alle drei Sprachen müssen ausgefüllt werden (DE, EN, IT).
                            </div>
                        </div>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-neutral-700 mb-1">Titelbild <span className="text-red-500">*</span></label>
                                <input
                                    type="file"
                                    onChange={(e) => setCoverImageFile(e.target.files[0])}
                                    className="w-full text-sm file:mr-3 file:py-2 file:px-3 file:border file:border-neutral-200 file:rounded-lg file:bg-neutral-50 file:text-neutral-700 hover:file:bg-neutral-100 cursor-pointer"
                                />
                            </div>
                            {coverPreview && (
                                <div className="relative h-32 w-full rounded-lg overflow-hidden border border-neutral-200 shadow-inner">
                                    <img src={coverPreview} alt="Preview" className="w-full h-full object-cover" />
                                    <button type="button" onClick={() => { setCoverImageFile(null); setCoverPreview(null); }} className="absolute top-1.5 right-1.5 bg-black/50 hover:bg-black/60 text-white rounded-full p-1 transition">
                                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 18 18 6M6 6l12 12" /></svg>
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-4">
                        <button type="submit" disabled={isLoading} className="inline-flex items-center justify-center px-6 h-11 rounded-lg bg-primary-600 hover:bg-primary-500 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium shadow-sm shadow-primary-500/25 transition">
                            {isLoading ? 'Verarbeite...' : 'Ordner erstellen'}
                        </button>
                        <button type="button" onClick={() => { setNewFolder({ description_de: '', description_en: '', description_it: '' }); setCoverImageFile(null); setCoverPreview(null); }} className="text-sm font-medium text-neutral-500 hover:text-neutral-700 transition">Zurücksetzen</button>
                    </div>
                </div>
            </form>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {folders.map(folder => (
                    <FolderCard key={folder.id} folder={folder} />
                ))}
                {folders.length === 0 && !isLoading && (
                    <div className="col-span-full bg-white border border-dashed border-neutral-300 rounded-2xl p-12 flex flex-col items-center justify-center text-center text-neutral-500">
                        <svg className="w-12 h-12 mb-4 text-neutral-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 7v10a2 2 0 0 0 2 2h6" /><path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v3" /></svg>
                        <p className="text-sm font-medium">Noch keine Ordner vorhanden.</p>
                        <p className="text-xs mt-2 max-w-sm">Erstellen Sie Ihren ersten Galerie-Ordner mit einem Titelbild – danach können Sie Medien zuordnen.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default GalleryFolderManager;
